import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'

import CancelSubscriptionModal from './CancelSubscriptionModal'
import { baseURL } from '../../config'

const formatDate = value =>
	value
		? new Date(value).toLocaleDateString(undefined, {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
		  })
		: null

const formatAmount = (amount, currency) =>
	amount == null
		? null
		: new Intl.NumberFormat(undefined, {
				style: 'currency',
				currency: (currency || 'usd').toUpperCase(),
		  }).format(amount)

function SubscriptionSettings({ isDarkMode, hideHeading }) {
	const navigate = useNavigate()
	const [subscription, setSubscription] = useState(null)
	const [loading, setLoading] = useState(true)
	const [loadError, setLoadError] = useState('')
	const [showCancelModal, setShowCancelModal] = useState(false)
	const [actionLoading, setActionLoading] = useState(false)
	const [actionError, setActionError] = useState('')
	const [portalLoading, setPortalLoading] = useState(false)

	const textStyle = { color: isDarkMode ? 'white' : 'black' }
	const mutedClass = isDarkMode ? 'text-gray-400' : 'text-gray-500'
	const cardClass = `rounded-lg border p-5 ${
		isDarkMode ? 'border-white/10 bg-gray-900/40' : 'border-gray-200 bg-gray-50'
	}`

	const fetchSubscription = async () => {
		try {
			const res = await axios.get(`${baseURL}/api/stripe/subscription`)
			setSubscription(res.data)
			setLoadError('')
		} catch (err) {
			console.error('Error fetching subscription:', err)
			setLoadError(
				err.response?.data?.error || 'Could not load your subscription',
			)
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		fetchSubscription()
	}, [])

	const handleCancel = async () => {
		setActionLoading(true)
		setActionError('')
		try {
			const res = await axios.post(`${baseURL}/api/stripe/cancel-subscription`)
			setSubscription(res.data)
			setShowCancelModal(false)
		} catch (err) {
			setActionError(
				err.response?.data?.error || 'Failed to cancel subscription',
			)
		} finally {
			setActionLoading(false)
		}
	}

	const handleResume = async () => {
		setActionLoading(true)
		setActionError('')
		try {
			const res = await axios.post(`${baseURL}/api/stripe/resume-subscription`)
			setSubscription(res.data)
		} catch (err) {
			setActionError(
				err.response?.data?.error || 'Failed to resume subscription',
			)
		} finally {
			setActionLoading(false)
		}
	}

	const openStripePortal = async portalRoute => {
		setPortalLoading(true)
		try {
			// Relative so the server resolves it against the request origin,
			// rather than depending on the browser's origin being present in
			// the allowed-host list.
			const returnUrl = encodeURIComponent('/settings/subscription')
			const res = await axios.get(
				`${baseURL}/api/stripe/${portalRoute}?returnUrl=${returnUrl}`,
			)
			window.location.href = res.data
		} catch (err) {
			console.error('Error opening billing portal:', err)
			setActionError('Could not open the billing portal')
			setPortalLoading(false)
		}
	}

	if (loading) {
		return (
			<div className="flex justify-center items-center py-32">
				<div
					className={`animate-spin rounded-full h-12 w-12 border-4 border-t-transparent ${
						isDarkMode ? 'border-gray-400' : 'border-gray-500'
					}`}
				/>
			</div>
		)
	}

	if (loadError) {
		return (
			<div className="max-w-2xl mx-auto py-12 text-center">
				<p className="text-red-500 mb-4">{loadError}</p>
				<button
					onClick={() => {
						setLoading(true)
						fetchSubscription()
					}}
					className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
				>
					Try again
				</button>
			</div>
		)
	}

	if (subscription?.managedByTeamOwner) {
		return (
			<div className="max-w-2xl mx-auto" style={{ maxWidth: '800px' }}>
				{!hideHeading && (
				<h2 style={textStyle} className="text-lg font-semibold mb-4">
					Subscription
				</h2>
				)}
				<div className={cardClass}>
					<p style={textStyle} className="text-sm mb-2">
						Your access is provided by{' '}
						<span className="font-semibold">
							{subscription.teamName || 'your team'}
						</span>
						.
					</p>
					<p className={`text-sm ${mutedClass}`}>
						Billing for this account is managed by the team owner. To change or
						cancel it, contact them directly.
					</p>
					<button
						onClick={() => navigate('/settings/team')}
						className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors text-sm"
					>
						View team settings
					</button>
				</div>
			</div>
		)
	}

	const isFree = !subscription || subscription.status === 'none'

	if (isFree) {
		return (
			<div className="max-w-2xl mx-auto" style={{ maxWidth: '800px' }}>
				{!hideHeading && (
				<h2 style={textStyle} className="text-lg font-semibold mb-4">
					Subscription
				</h2>
				)}
				<div className={cardClass}>
					<p style={textStyle} className="text-base font-semibold mb-1">
						Free plan
					</p>
					<p className={`text-sm mb-4 ${mutedClass}`}>
						You do not have an active paid subscription.
					</p>
					<button
						onClick={() => navigate('/payment')}
						className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors text-sm"
					>
						View plans
					</button>
				</div>
			</div>
		)
	}

	const renewalDate = formatDate(subscription.currentPeriodEnd)
	const price = formatAmount(subscription.amount, subscription.currency)
	const period = subscription.cycle === 'yearly' ? 'year' : 'month'
	const isCancelling = subscription.cancelAtPeriodEnd

	return (
		<div className="max-w-2xl mx-auto" style={{ maxWidth: '800px' }}>
			{!hideHeading && (
			<h2 style={textStyle} className="text-lg font-semibold mb-4">
				Subscription
			</h2>
			)}

			{isCancelling && (
				<div className="rounded-lg border border-orange-500/40 bg-orange-500/10 p-4 mb-4">
					<p className="text-sm font-semibold text-orange-400 mb-1">
						Your subscription is scheduled to cancel
					</p>
					<p className={`text-sm ${mutedClass}`}>
						You keep full access until {renewalDate}. You will not be charged
						again.
					</p>
				</div>
			)}

			<div className={cardClass}>
				<div className="flex items-start justify-between gap-4 mb-4">
					<div>
						<p style={textStyle} className="text-base font-semibold">
							{subscription.plan}
							{subscription.cycle && (
								<span className={`ml-2 text-sm font-normal ${mutedClass}`}>
									{subscription.cycle === 'yearly' ? 'Yearly' : 'Monthly'}
								</span>
							)}
						</p>
						{price && (
							<p className={`text-sm mt-1 ${mutedClass}`}>
								{subscription.seats > 1 ? (
									<>
										{formatAmount(
											subscription.amount * subscription.seats,
											subscription.currency,
										)}{' '}
										per {period} · {subscription.seats} seats at {price} each
									</>
								) : (
									<>
										{price} per {period}
									</>
								)}
							</p>
						)}
					</div>
					<span
						className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${
							isCancelling
								? 'bg-orange-500/15 text-orange-400'
								: 'bg-green-500/15 text-green-400'
						}`}
					>
						{isCancelling ? 'Cancelling' : 'Active'}
					</span>
				</div>

				<dl className="space-y-2 text-sm">
					<div className="flex justify-between">
						<dt className={mutedClass}>
							{isCancelling ? 'Access until' : 'Renews on'}
						</dt>
						<dd style={textStyle}>{renewalDate || 'Unknown'}</dd>
					</div>
					{subscription.trialEnd && (
						<div className="flex justify-between">
							<dt className={mutedClass}>Trial ends</dt>
							<dd style={textStyle}>{formatDate(subscription.trialEnd)}</dd>
						</div>
					)}
					{isCancelling && subscription.canceledAt && (
						<div className="flex justify-between">
							<dt className={mutedClass}>Cancelled on</dt>
							<dd style={textStyle}>{formatDate(subscription.canceledAt)}</dd>
						</div>
					)}
				</dl>
			</div>

			{actionError && (
				<p className="text-sm text-red-500 mt-4">{actionError}</p>
			)}

			<div className="mt-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
				{isCancelling ? (
					<button
						onClick={handleResume}
						disabled={actionLoading}
						className="w-full sm:w-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors text-sm disabled:opacity-50"
					>
						{actionLoading ? 'Resuming...' : 'Resume subscription'}
					</button>
				) : (
					<button
						onClick={() => openStripePortal('customer-portal-plans')}
						disabled={portalLoading}
						className="w-full sm:w-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors text-sm disabled:opacity-50"
					>
						Change plan
					</button>
				)}

				<button
					onClick={() => openStripePortal('customer-portal')}
					disabled={portalLoading}
					className={`w-full sm:w-auto px-4 py-2 rounded-md transition-colors text-sm disabled:opacity-50 ${
						isDarkMode
							? 'bg-gray-700 text-white hover:bg-gray-600'
							: 'bg-gray-100 text-gray-700 hover:bg-gray-200'
					}`}
				>
					{portalLoading ? 'Opening...' : 'Payment method & invoices'}
				</button>

				{!isCancelling && (
					<button
						onClick={() => {
							setActionError('')
							setShowCancelModal(true)
						}}
						className={`flex min-h-[44px] w-full items-center justify-center text-sm underline underline-offset-2 hover:opacity-80 sm:ml-auto sm:w-auto ${mutedClass}`}
					>
						Cancel subscription
					</button>
				)}
			</div>

			{showCancelModal && (
				<CancelSubscriptionModal
					isDarkMode={isDarkMode}
					plan={subscription.plan}
					accessUntil={renewalDate}
					seats={subscription.seats}
					isCancelling={actionLoading}
					error={actionError}
					onConfirm={handleCancel}
					onClose={() => setShowCancelModal(false)}
				/>
			)}
		</div>
	)
}

export default SubscriptionSettings
